import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  SessionResult,
  CompletedSessionHistoryResponse,
  EditSessionRequest,
  JoinSessionResponse,
  StaticWeatherConfig,
  StartSessionRequest,
  StartSessionResponse,
  StartStintRequest,
  Stint,
} from '@spectrace/shared';
import { ApiService } from './api.service';
import { TeamContextService } from './team-context.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private activeSessionSubject = new BehaviorSubject<SessionResult | null>(
    null,
  );
  public activeSession$ = this.activeSessionSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private teamContextService: TeamContextService,
    private authService: AuthService,
  ) {
    this.authService.currentUser$.subscribe((user) => {
      if (!user) {
        this.clearActiveSession();
      }
    });
  }

  setActiveSession(session: SessionResult | null): void {
    this.activeSessionSubject.next(session);
  }

  getActiveSessionSnapshot(): SessionResult | null {
    return this.activeSessionSubject.value;
  }

  clearActiveSession(): void {
    this.activeSessionSubject.next(null);
  }

  startSession(
    name: string,
    trackName: string,
    trackId: number,
    isPublic?: boolean,
    startTime?: string,
    endTime?: string,
    lapsPerDriver?: number,
    allowedCarClasses?: string[],
    allowSetupChanges?: boolean,
    staticWeatherConfig?: StaticWeatherConfig,
    minIRating?: number,
    maxIRating?: number,
    seasonId?: string,
  ): Observable<StartSessionResponse> {
    const teamId = this.teamContextService.getCurrentTeamId() || '';

    const request: StartSessionRequest = {
      teamId,
      name,
      trackName,
      trackId,
      isPublic,
      startTime,
      endTime,
      lapsPerDriver,
      allowedCarClasses,
      allowSetupChanges,
      staticWeatherConfig,
      minIRating,
      maxIRating,
      seasonId,
    };

    return this.apiService.startSession(request).pipe(
      tap((response) => {
        this.setActiveSession(response.session);
      }),
    );
  }

  joinSession(sessionId: string): Observable<JoinSessionResponse> {
    return this.apiService.joinSession(sessionId).pipe(
      tap((response) => {
        if (response?.session) {
          this.setActiveSession(response.session);
        }
      }),
    );
  }

  editSession(
    sessionId: string,
    trackName: string,
    trackId: number,
    lapsPerDriver: number,
    allowedCarClasses?: string[],
    allowSetupChanges?: boolean,
    staticWeatherConfig?: StaticWeatherConfig,
    minIRating?: number,
    maxIRating?: number,
    startTime?: string,
    endTime?: string,
  ): Observable<{ session: SessionResult }> {
    const request: EditSessionRequest = {
      trackName,
      trackId,
      lapsPerDriver,
      allowedCarClasses,
      allowSetupChanges,
      staticWeatherConfig,
      minIRating,
      maxIRating,
      startTime,
      endTime,
    };
    return this.apiService.editSession(sessionId, request);
  }

  endSession(): Observable<{ success: boolean }> {
    return this.apiService.endSession();
  }

  startStint(playerId: string): Observable<{ stint: Stint }> {
    const request: StartStintRequest = { playerId };
    return this.apiService.startStint(request);
  }

  endStint(): Observable<{ success: boolean }> {
    return this.apiService.endStint();
  }

  endStintById(stintId: string): Observable<{ success: boolean }> {
    return this.apiService.endStintById(stintId);
  }

  getStints(sessionId?: string): Observable<{ stints: Stint[] }> {
    return this.apiService.getStints(sessionId);
  }

  getSessionById(sessionId: string): Observable<{ session: any }> {
    return this.apiService.getSessionById(sessionId);
  }

  getSessionParticipants(
    sessionId: string,
  ): Observable<{ participants: any[] }> {
    return this.apiService.getSessionParticipants(sessionId);
  }

  getCompletedSessionHistory(params?: {
    teamId?: string;
    limit?: number;
    offset?: number;
    cursor?: string;
  }): Observable<CompletedSessionHistoryResponse> {
    return this.apiService.getCompletedSessionHistory(params);
  }
}
